/* eslint-disable array-bracket-newline */
import { useEffect, useState } from 'react';
import { ITicker } from '@kattana-io/common';
import Text from '../../atoms/Text';
import { IMobileFilterProps } from '../SearchTable/SearchTable';
import { getPriceChange } from '../../helpers/priceChange';
import useTicker from '../../hooks/useTicker';

import './styles.scss';

enum SortOption {
  Unset = '',
  ABC = 'ABC',
  LowPrice = 'Low price',
  PriceAlerts = 'Price alerts',
  HighPrice = 'High price',
  PriceChanges = 'Price changes',
}

type sortOptionsType = {
  icon: string;
  title: SortOption;
}[];

const sortOptions: sortOptionsType = [
  {
    icon: 'icon-sort',
    title: SortOption.ABC,
  },
  {
    icon: 'icon-sort-arrow-down',
    title: SortOption.LowPrice,
  },
  {
    icon: 'icon-sort-arrow-up',
    title: SortOption.HighPrice,
  },
  {
    icon: 'icon-sort-two-arrow',
    title: SortOption.PriceChanges,
  },
];

export interface IMobileTickersSortProps extends IMobileFilterProps {
  initialData: {
    tickers: ITicker[];
    selected: SortOption;
  };
}

const MobileTickersSort = ({
  initialData,
  setSortValue,
}: IMobileTickersSortProps) => {
  const [active, setActive] = useState<SortOption>(initialData.selected);

  const onOptionClick = (currentOption: SortOption) => {
    if (active === currentOption) {
      return setActive(SortOption.Unset);
    }
    setActive(currentOption);
  };

  useEffect(() => {
    const copiedTickers = [...initialData.tickers];
    if (!active) {
      setSortValue!({ tickers: copiedTickers, selected: SortOption.Unset });
    }
    if (active === SortOption.ABC) {
      const sorted = copiedTickers.sort((a, b) => {
        const aName = useTicker(a).name;
        const bName = useTicker(b).name;
        return aName.localeCompare(bName);
      });
      setSortValue!({ tickers: sorted, selected: SortOption.ABC });
    }
    if (active === SortOption.LowPrice) {
      const sorted = copiedTickers.sort((a, b) => {
        const aPrice = a.flipped ? a.price_b_usd : a.price_a_usd;
        const bPrice = b.flipped ? b.price_b_usd : b.price_a_usd;
        return bPrice - aPrice;
      });
      setSortValue!({ tickers: sorted, selected: SortOption.LowPrice });
    }
    if (active === SortOption.HighPrice) {
      const sorted = copiedTickers.sort((a, b) => {
        const aPrice = a.flipped ? a.price_b_usd : a.price_a_usd;
        const bPrice = b.flipped ? b.price_b_usd : b.price_a_usd;
        return aPrice - bPrice;
      });
      setSortValue!({ tickers: sorted, selected: SortOption.HighPrice });
    }
    if (active === SortOption.PriceChanges) {
      const sorted = copiedTickers.sort((a, b) => {
        const aTicker = useTicker(a);
        const bTicker = useTicker(b);
        const { priceChangePercent: aPriceChange } = getPriceChange(
          aTicker.price_usd,
          a
        );
        const { priceChangePercent: bPriceChange } = getPriceChange(
          bTicker.price_usd,
          b
        );
        return bPriceChange - aPriceChange;
      });
      setSortValue!({ tickers: sorted, selected: SortOption.PriceChanges });
    }
  }, [active, initialData, setSortValue]);

  return (
    <>
      {sortOptions.map((option) => {
        const isActive = option.title === active;
        return (
          <div
            key={option.icon}
            className={`mobile_sort__item${
              isActive ? ' mobile_sort__item--active' : ''
            }`}
            onClick={() => onOptionClick(option.title)}
          >
            <div className='mobile_sort__item__content'>
              <div className={`mobile_sort__item__icon ${option.icon}`} />
              <Text
                size={14}
                className={`text--weight-500 ${
                  isActive ? 'text--grey-200' : 'text--grey-400'
                }`}
              >
                {option.title}
              </Text>
            </div>
            {isActive && (
              <div className='mobile_sort__item__icon mobile_sort__item__icon--active icon-check' />
            )}
          </div>
        );
      })}
    </>
  );
};

export default MobileTickersSort;
